import { getSelectedContext } from "@/lib/api";
import type { OrderType } from "@/components/orders/OrderTypeTabs";

/**
 * POS "where was I" state (last table, order, order type), kept per tab in
 * sessionStorage. Keys are scoped by outlet: after "Switch Outlet/Staff" the
 * POS used to reopen the previous outlet's table (and could seat it).
 */
const LAST_TABLE = "orders_last_active_table";
const LAST_ORDER_TYPE = "orders_last_order_type";
const LAST_ORDER_ID = "orders_last_order_id";
const LAST_VIEW = "orders_last_view";
const CATEGORY_EXPANDED = "orders_category_expanded";
const BASES = [LAST_TABLE, LAST_ORDER_TYPE, LAST_ORDER_ID, LAST_VIEW, CATEGORY_EXPANDED];

const scoped = (base: string): string => `${base}:${getSelectedContext()?.outlet?.id ?? "none"}`;

function read(base: string): string | null {
  try {
    return sessionStorage.getItem(scoped(base));
  } catch {
    return null;
  }
}

function write(base: string, value: string | null) {
  try {
    if (value === null) sessionStorage.removeItem(scoped(base));
    else sessionStorage.setItem(scoped(base), value);
  } catch {
    // storage unavailable (private mode / quota): the POS just won't restore
  }
}

export function getLastTable(): { id: string; number: string } | null {
  const raw = read(LAST_TABLE);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export const saveLastTable = (table: { id: string; number: string } | null) =>
  write(LAST_TABLE, table ? JSON.stringify(table) : null);

export function getLastOrderType(): OrderType | null {
  const v = read(LAST_ORDER_TYPE);
  return v === "dine-in" || v === "pickup" || v === "delivery" || v === "qsr" ? v : null;
}

export const saveLastOrderType = (type: OrderType | null) => write(LAST_ORDER_TYPE, type);

export const getLastOrderId = (): string | null => read(LAST_ORDER_ID);

export const saveLastOrderId = (id: string | null) => write(LAST_ORDER_ID, id);

/** Orders screen view: the menu, or the Active Orders board. */
export type OrdersView = "menu" | "board";

export const getLastOrdersView = (): OrdersView => (read(LAST_VIEW) === "board" ? "board" : "menu");

export const saveLastOrdersView = (view: OrdersView) => write(LAST_VIEW, view === "board" ? "board" : null);

/** Category groups the cashier opened (true) or closed (false) in the Orders category column. */
export function getCategoryExpanded(): Record<string, boolean> {
  const raw = read(CATEGORY_EXPANDED);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, boolean] => typeof entry[1] === "boolean"),
    );
  } catch {
    return {};
  }
}

export const saveCategoryExpanded = (expanded: Record<string, boolean>) =>
  write(CATEGORY_EXPANDED, Object.keys(expanded).length ? JSON.stringify(expanded) : null);

/** Forget POS state for every outlet (and the old unscoped keys). Call on context switch. */
export function clearPosSessionStorage() {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && BASES.some((b) => key === b || key.startsWith(`${b}:`))) doomed.push(key);
    }
    doomed.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}
